// O formulário de classificação de rubrica — extraído do Motor de Rubricas.
//
// POR QUE SAIU DE LÁ. Classificar é o ÚNICO caminho de saída da trava de margem:
// uma verba classificada libera os vínculos que ela segurava. A tela de trava
// precisa oferecer esse caminho sem mandar o operador para outra rota — e sem
// reimplementar o formulário. Desde a #270 os CAMPOS da classificação (categoria
// legal, tipo de serviço, produto, consignatária, credor final, comportamento)
// moram em `classificacao-campos.tsx`, que este modal e o `CardClassificacao`
// da conciliação hospedam: as duas versões divergentes viraram uma.
//
// TRÊS ACOPLAMENTOS FORAM DESFEITOS, e nenhum era regra de negócio — essa mora
// toda no servidor, atrás de `rubricas.confirmar`:
//
//  1. `vinculoAtivo` era calculado pelo pai e passado pronto. Quem quisesse
//     reusar precisaria repetir esse cálculo, que é a duplicação que a extração
//     existe para evitar. Agora o componente o resolve sozinho a partir de
//     `folhaId` + `codigoVerba` quando a prop não vem.
//  2. A invalidação de cache era `["admin","rubricas"]` fixa. A tela de trava
//     precisa invalidar também impacto e pendências, senão a fila continua
//     mostrando uma verba que acabou de ser classificada.
//  3. O feedback de sucesso era `window.alert`. Virou `onSuccess`, e quem
//     hospeda decide como mostrar.
//
// O Motor de Rubricas continua passando `vinculoAtivo` por prop e não muda de
// comportamento — é o que o teste de regressão verifica.

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Button, FormGrid, SelectField, TextField } from "@atlas/ui/web";
import { categoriaDaNatureza } from "@atlas/domain";
import type { AdminRubrica, AdminRubricaVinculo } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { QUERY_CREDORES_OPERAVEIS, listarCredoresOperaveis } from "../../lib/credores-operaveis";
import { Modal } from "../prefeitura/_ui";
import { CATEGORIA_LABEL, fmtVigencia } from "./motor-rubricas-labels";
import { CamposClassificacao, classificacaoVigente, useCamposClassificacao } from "./classificacao-campos";

/** A competência corrente, para pré-preencher a troca de vigência. */
function competenciaAtual(): string {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export interface ModalClassificacaoProps {
  folhaId: number;
  rubrica: AdminRubrica | null;
  /**
   * Código da verba, quando o hospedeiro não tem o objeto `rubrica` em mão.
   *
   * É o caso da fila da trava: ela conhece o código pendente, não a linha do
   * catálogo. Serve para resolver a vigência ativa sem obrigar o hospedeiro a
   * refazer o cálculo do Motor de Rubricas.
   */
  codigoVerba?: string;
  /** Vigência ativa. Ausente = o componente resolve a partir de `codigoVerba`. */
  vinculoAtivo?: AdminRubricaVinculo | null;
  onClose: () => void;
  /** Chamado depois de salvar. Recebe quantas dívidas foram reapontadas. */
  onSuccess?: (r: { dividasReclassificadas: number; descontosPromovidos: number }) => void;
  /**
   * Chaves de cache a invalidar além de `["admin","rubricas"]`.
   *
   * A tela de trava passa as chaves de impacto e de pendências: sem isso a fila
   * não reflete a classificação que o operador acabou de fazer.
   */
  invalidateKeys?: readonly unknown[][];
}

/**
 * O formulário de classificação — e a decisão mais delicada da tela.
 *
 * Quando a rubrica JÁ tem vigência ativa, editar tem dois significados
 * diferentes, e só um preserva história:
 *
 *   CORRIGIR   "sempre foi assim, estava gravado errado" — ajusta a vigência
 *              atual. É o erro de digitação percebido cinco minutos depois.
 *   SUCEDER    "mudou a partir de tal mês" — encerra a vigência atual no mês
 *              anterior e abre outra. É a prefeitura trocando de consignatária.
 *
 * A escolha é EXPLÍCITA porque adivinhar erra dos dois lados: corrigir quando
 * era troca reatribuiria repasse já feito; suceder quando era erro inventaria no
 * histórico uma troca de banco que ninguém fez.
 */
export function ModalClassificacao(props: ModalClassificacaoProps) {
  const { folhaId, rubrica, codigoVerba, vinculoAtivo: vinculoPorProp, onClose } = props;

  // Resolve a vigência ativa quando o hospedeiro não a passou. A query é a mesma
  // do Motor de Rubricas, então o React Query a deduplica quando os dois estão
  // montados; e ela só roda quando é preciso.
  const precisaResolver = vinculoPorProp === undefined;
  const catalogo = useQuery({
    queryKey: ["admin", "rubricas", folhaId],
    queryFn: () => atlas.admin.rubricas.list(folhaId),
    enabled: precisaResolver && folhaId > 0,
  });
  const vinculoAtivo = useMemo<AdminRubricaVinculo | null>(() => {
    if (!precisaResolver) return vinculoPorProp ?? null;
    const alvo = (rubrica?.codigoOriginal ?? codigoVerba ?? "").replace(/^0+/, "");
    if (!alvo) return null;
    // Mesma regra do pai (`ativoPorCodigo`): a vigência que está valendo agora.
    return (
      catalogo.data?.vinculos.find(
        (v) => v.situacao === "ativo" && String(v.codigoVerba).replace(/^0+/, "") === alvo,
      ) ?? null
    );
  }, [precisaResolver, vinculoPorProp, rubrica?.codigoOriginal, codigoVerba, catalogo.data]);

  // O formulário só MONTA depois de a vigência resolver. Na fila da trava ela
  // chega depois da montagem; um formulário montado antes leria "sem vínculo" no
  // estado inicial e ofereceria uma verba já tipada com o tipo vazio. A `key`
  // remonta se a vigência trocar — nenhum efeito sobrescreve o que o operador
  // já escolheu.
  const resolvendo = precisaResolver && folhaId > 0 && catalogo.isLoading;
  return (
    <Modal
      title={rubrica ? `Rubrica ${rubrica.codigoOriginal}` : "Nova rubrica"}
      onClose={onClose}
      maxWidth={620}
    >
      {resolvendo ? (
        <div style={{ padding: 16, color: "var(--text-dim)", fontSize: 13 }}>Carregando a vigência…</div>
      ) : (
        <FormularioClassificacao key={vinculoAtivo?.id ?? "sem-vigencia"} {...props} vinculoAtivo={vinculoAtivo} />
      )}
    </Modal>
  );
}

function FormularioClassificacao({
  folhaId,
  rubrica,
  codigoVerba,
  vinculoAtivo,
  onClose,
  onSuccess,
  invalidateKeys,
}: ModalClassificacaoProps & { vinculoAtivo: AdminRubricaVinculo | null }) {
  const qc = useQueryClient();
  const bancos = useQuery({ queryKey: QUERY_CREDORES_OPERAVEIS, queryFn: listarCredoresOperaveis });

  const [codigo, setCodigo] = useState(rubrica?.codigoOriginal ?? codigoVerba ?? "");
  const [descricao, setDescricao] = useState(rubrica?.descricao ?? "");
  // A classificação parte do VÍNCULO ATIVO (é ele que sabe categoria, tipo e
  // credor final); rubrica não classificada abre VAZIA e a sugestão vira dica
  // (#176); rubrica nova nasce em crédito. Ver `classificacaoVigente`.
  const [inicial] = useState(() => classificacaoVigente(rubrica, vinculoAtivo));
  const [modo, setModo] = useState<"corrigir" | "suceder">("corrigir");
  const [competencia, setCompetencia] = useState(competenciaAtual());
  const [erro, setErro] = useState<string | null>(null);
  // Um convênio declara UMA verba por produto. A consignatária que responde por
  // mais de uma (ASSALCE: 807 e 739, as duas de empréstimo) tem as outras fora dele.
  const [foraDoConvenio, setForaDoConvenio] = useState(false);
  // COMPORTAMENTO (#165): se a facultativa se repete no mês seguinte. Rubrica
  // que já tem declaração abre com a declarada.
  const codigoDaRubrica = (rubrica?.codigoOriginal ?? codigoVerba ?? "").replace(/^0+(?=\d)/, "");
  const declarados = useQuery({
    queryKey: ["admin", "rubricas", "comportamentos", folhaId],
    queryFn: () => atlas.admin.rubricas.comportamentos(folhaId),
    enabled: rubrica !== null,
  });
  const declarado = declarados.data?.comportamentos
    .filter((d) => d.rubrica === codigoDaRubrica && d.competenciaFim === null)
    .at(-1)?.comportamento;
  const c = useCamposClassificacao(inicial, declarado);
  const credorId = c.consignatariaId;

  // DESCRIÇÃO E CLASSIFICAÇÃO SÃO DUAS ESCRITAS. Trocar o nome não é
  // reclassificar: se só a descrição mudou, o `confirmar` nem é chamado — ele
  // "corrigiria" a vigência sem nada ter mudado, e rubrica ainda não
  // classificada nem teria como ganhar nome (é a verba que trava o DESCRUB).
  const descricaoMudou = rubrica !== null && descricao.trim() !== rubrica.descricao.trim();
  // Compara com o MESMO ponto de partida do formulário (`inicial`), e não com a
  // rubrica crua: senão um vínculo com tipo, ou um desconto obrigatório, pareceria
  // sempre "mudado" e só trocar o nome reclassificaria.
  const classificacaoMudou =
    rubrica === null || c.mudou || foraDoConvenio || (vinculoAtivo !== null && modo === "suceder");
  // Sem mudar a descrição, o Salvar confirma como sempre confirmou.
  const confirma = c.categoria !== "" && (!descricaoMudou || classificacaoMudou);

  const salvar = useMutation({
    mutationFn: async () => {
      setErro(null);
      if (descricaoMudou) await atlas.admin.rubricas.alterarDescricao(rubrica!.id, descricao.trim());
      if (!confirma) return null;
      return atlas.admin.rubricas.confirmar({
        id: rubrica?.id ?? `${folhaId}:${codigo.replace(/^0+/, "") || "0"}`,
        unidadeId: rubrica ? undefined : folhaId,
        codigoVerba: rubrica ? undefined : codigo,
        descricao: rubrica ? undefined : descricao,
        // O corpo novo (#270): categoria, tipo, produto, consignatária, credor
        // final e comportamento. CREDOR OPCIONAL (ADR-0010): sem ele a dívida
        // nasce "a identificar" e o convênio liga depois.
        ...c.payload(bancos.data?.bancos ?? []),
        foraDoConvenio: c.credito && Boolean(credorId) && !c.criandoCredor && foraDoConvenio,
        // A competência só viaja no modo SUCEDER. Mandá-la sempre inventaria
        // trocas de banco que nunca aconteceram.
        competenciaInicial: vinculoAtivo && modo === "suceder" ? competencia : undefined,
      });
    },
    // Invalida também no erro: a descrição pode ter sido gravada e a
    // classificação recusada logo depois, e a lista precisa mostrar o nome novo.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["admin", "rubricas"] });
      // A consignatária cadastrada aqui tem que aparecer na próxima classificação.
      if (c.cadastraCredor) qc.invalidateQueries({ queryKey: ["admin", "bancos"] });
      for (const chave of invalidateKeys ?? []) qc.invalidateQueries({ queryKey: chave });
    },
    onSuccess: (r) => {
      if (!r) {
        onClose();
        return;
      }
      if (onSuccess) {
        onSuccess({
          dividasReclassificadas: r.dividasReclassificadas,
          descontosPromovidos: r.descontosPromovidos,
        });
      } else if (r.dividasReclassificadas > 0) {
        // Comportamento herdado, preservado para quem não passa `onSuccess` —
        // o Motor de Rubricas depende dele.
        window.alert(
          `${r.dividasReclassificadas} dívida(s) desta rubrica tiveram o credor/produto reapontados.`,
        );
      }
      onClose();
    },
    onError: (e) => setErro(e instanceof Error ? e.message : "Não foi possível salvar."),
  });

  // Credor deixou de ser obrigatório em 10/09/2026 (ADR-0010). O que continua
  // obrigatório para virar dívida é o PRODUTO — sem produto não há margem a abater.
  const novaSemCodigo = !rubrica && codigo.trim() === "";
  const sugestao =
    rubrica && rubrica.natureza === "nao_classificada" && rubrica.naturezaSugerida !== "nao_classificada"
      ? CATEGORIA_LABEL[categoriaDaNatureza(rubrica.naturezaSugerida)]
      : null;

  return (
      <div style={{ display: "grid", gap: 12 }}>
        {/* SAÍDA DE EMERGÊNCIA, e o texto diz isso.
            Desde a feature 004 o caminho normal é declarar a verba no cadastro
            do convênio: lá o significado nasce ligado ao banco que recebe o
            repasse, e a folha passa a reconhecer o desconto sem segunda tela.
            Classificar aqui continua existindo — é o que resolve credor sem
            convênio e o que salva o operador num sábado —, mas o que é
            declarado aqui não tem convênio que o explique, e a tela passa a
            listar essas verbas como pendência (FR-045). */}
        <div
          style={{
            padding: "8px 10px",
            borderRadius: 8,
            fontSize: 12,
            lineHeight: 1.5,
            color: "var(--text-muted)",
            background: "color-mix(in srgb, var(--gold-500) 8%, transparent)",
            border: "1px solid color-mix(in srgb, var(--gold-500) 30%, transparent)",
          }}
        >
          <strong style={{ color: "var(--gold-500)" }}>Saída de emergência.</strong> O caminho normal é
          declarar a verba no{" "}
          <Link to="/averbadora/convenios" style={{ color: "var(--gold-500)" }}>
            cadastro do convênio
          </Link>{" "}
          — lá ela já nasce ligada à consignatária que recebe o repasse. Use esta tela quando não houver convênio:
          credor que só é dono de dívida antiga, ou uma correção que não pode esperar.
        </div>

        {rubrica ? (
          <TextField
            label="Descrição"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            maxLength={120}
            placeholder="DESCONTO EMP CONSIG BB"
            hint={
              rubrica.descricao.trim() === ""
                ? "Sem descrição, o movimento sai com o DESCRUB vazio e a emissão é recusada."
                : "É o DESCRUB do movimento. Se o próximo retorno trouxer outro texto para a verba, ele substitui este."
            }
          />
        ) : (
          <FormGrid cols={2}>
            <TextField
              label="Código da rubrica"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              placeholder="0234"
              required
              hint="Como a folha usa, com os zeros à esquerda."
            />
            <TextField
              label="Descrição"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="DESCONTO EMP CONSIG BB"
            />
          </FormGrid>
        )}

        <CamposClassificacao c={c} bancos={bancos.data?.bancos ?? []} sugestao={sugestao} />

        {c.credito && credorId && !c.criandoCredor ? (
          <label
            style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, color: "var(--text-muted)", cursor: "pointer" }}
          >
            <input
              type="checkbox"
              checked={foraDoConvenio}
              onChange={(e) => setForaDoConvenio(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span>
              <b style={{ color: "var(--text)" }}>Verba fora do convênio</b>
              <br />
              O convênio declara uma verba por produto. Quando o credor responde por mais de uma — verba legada,
              ou administrada por uma consignatária —, as outras ficam fora dele: o convênio segue com a verba
              dele para averbações novas, e esta fica no catálogo com o mesmo credor.
            </span>
          </label>
        ) : null}

        {vinculoAtivo ? (
          <div
            style={{
              padding: 12,
              borderRadius: 10,
              background: "var(--bg-elev-2)",
              border: "1px solid var(--border)",
              display: "grid",
              gap: 10,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--text-dim)",
                  textTransform: "uppercase",
                  letterSpacing: ".06em",
                }}
              >
                Vigência atual
              </div>
              <div style={{ fontSize: 13, marginTop: 2 }}>{fmtVigencia(vinculoAtivo)}</div>
            </div>
            <SelectField
              label="Esta mudança é…"
              value={modo}
              onChange={(e) => setModo(e.target.value as "corrigir" | "suceder")}
              options={[
                { value: "corrigir", label: "Uma correção — sempre foi assim, estava gravado errado" },
                { value: "suceder", label: "Uma mudança — passou a valer a partir de um mês" },
              ]}
              hint={
                modo === "corrigir"
                  ? "Ajusta a vigência atual. Use quando a classificação estava errada desde o início."
                  : "Encerra a vigência atual no mês anterior e abre outra. O passado continua respondendo o que respondia."
              }
            />
            {modo === "suceder" ? (
              <TextField
                label="Vale a partir da competência"
                value={competencia}
                onChange={(e) => setCompetencia(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="202609"
                required
                hint="AAAAMM. A vigência atual será encerrada no mês anterior a este."
              />
            ) : null}
          </div>
        ) : null}

        {erro ? (
          <div
            style={{
              padding: 10,
              borderRadius: 8,
              fontSize: 13,
              background: "color-mix(in srgb, var(--danger-500) 12%, transparent)",
            }}
          >
            {erro}
          </div>
        ) : null}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={
              novaSemCodigo ||
              salvar.isPending ||
              (descricaoMudou && descricao.trim() === "") ||
              (!confirma && !descricaoMudou) ||
              // A regra é a do domínio, a mesma que a API aplica: o que ela
              // recusaria, a tela nem deixa mandar.
              (confirma && !c.valida)
            }
            onClick={() => salvar.mutate()}
          >
            {salvar.isPending ? "Salvando…" : descricaoMudou && !confirma ? "Salvar descrição" : "Salvar classificação"}
          </Button>
        </div>
      </div>
  );
}
